import { z } from 'zod';

// ---------- Contractor schemas ----------
export const contractorCreateSchema = z.object({
  legalName: z.string().min(1, 'Legal name is required').max(255),
  tradingName: z.string().max(255).optional(),
  tin: z
    .string()
    .regex(/^\d{2}-?\d{7}$|^\d{3}-?\d{2}-?\d{4}$/, 'TIN must be SSN (XXX-XX-XXXX) or EIN (XX-XXXXXXX)')
    .optional(),
  tinType: z.enum(['SSN', 'EIN']).optional(),
  isExemptFrom1099: z.boolean().optional().default(false),
});

export const contractorUpdateSchema = z.object({
  legalName: z.string().min(1).max(255).optional(),
  tradingName: z.string().max(255).nullish(),
  tin: z
    .string()
    .regex(/^\d{2}-?\d{7}$|^\d{3}-?\d{2}-?\d{4}$/, 'TIN must be SSN (XXX-XX-XXXX) or EIN (XX-XXXXXXX)')
    .optional(),
  tinType: z.enum(['SSN', 'EIN']).optional(),
  isExemptFrom1099: z.boolean().optional(),
});

export type ContractorCreateInput = z.infer<typeof contractorCreateSchema>;
export type ContractorUpdateInput = z.infer<typeof contractorUpdateSchema>;

// ---------- Schedule C query ----------
export const scheduleCQuerySchema = z.object({
  taxYear: z.coerce.number().int().min(2020).max(2099),
});

export type ScheduleCQuery = z.infer<typeof scheduleCQuerySchema>;

// ---------- State estimate query ----------
export const stateEstimateQuerySchema = z.object({
  taxYear: z.coerce.number().int().min(2020).max(2099).optional(),
});

export type StateEstimateQuery = z.infer<typeof stateEstimateQuerySchema>;

// ---------- 1099 prep query ----------
export const form1099PrepQuerySchema = z.object({
  taxYear: z.coerce.number().int().min(2020).max(2099),
});

export type Form1099PrepQuery = z.infer<typeof form1099PrepQuerySchema>;

// ---------- Filing draft ----------
export const filingDraftQuerySchema = z.object({
  filingType: z.enum(['SCHEDULE_C', 'FORM_1099']).optional(),
  status: z.enum(['DRAFT', 'READY', 'FILED_VIA_INTEGRATION', 'FILED_EXTERNALLY', 'ARCHIVED']).optional(),
});

export type FilingDraftQuery = z.infer<typeof filingDraftQuerySchema>;

export const markFiledSchema = z.object({
  filedVia: z.enum(['qbo', 'manual']),
  filedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
  evidence: z.string().max(1000).optional(),
});

export type MarkFiledInput = z.infer<typeof markFiledSchema>;

// ---------- US org settings (tax fields) ----------
export const usTaxSettingsSchema = z.object({
  ein: z
    .string()
    .regex(/^\d{2}-?\d{7}$/, 'EIN must be XX-XXXXXXX')
    .nullish(),
  stateOfIncorporation: z
    .string()
    .length(2, 'Must be 2-letter state code')
    .toUpperCase()
    .nullish(),
  accountingBasis: z.enum(['cash', 'accruals']).nullish(),
});

export type UsTaxSettingsInput = z.infer<typeof usTaxSettingsSchema>;

// ---------- W-9 link ----------
export const linkW9Schema = z.object({
  documentId: z.string().min(1),
  legalName: z.string().min(1).optional(),
  tin: z.string().optional(),
  tinType: z.enum(['SSN', 'EIN']).optional(),
});

export type LinkW9Input = z.infer<typeof linkW9Schema>;
